import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { cn } from './cn';
import { useFieldWiring } from './fields';
import { useLayerContainer } from './layer';

export interface ComboboxOption<V extends string | number> {
  value: V;
  label: string;
  /** A second line, e.g. the plate or the IMEI. */
  detail?: string;
  /** Extra words the search should match. */
  keywords?: string;
  /** Rendered before the label (a sign, a plate). */
  icon?: ReactNode;
}

export interface ComboboxProps<V extends string | number> {
  value: V | null;
  onChange: (value: V | null) => void;
  options: readonly ComboboxOption<V>[];
  /** Shown while nothing is chosen. */
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  /** Offer a first "all/none" option with this label, which sets null. */
  nullLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;
  id?: string;
  className?: string;
  /** Classes for the popover (width). */
  contentClassName?: string;
  /** Before the value in the closed field (a search icon). */
  leading?: ReactNode;
  /** After it, instead of the chevron (a key hint). */
  trailing?: ReactNode;
  triggerRef?: Ref<HTMLButtonElement>;
  /** The trigger's accessible name when no label points at it. */
  ariaLabel?: string;
}

const MAX_SHOWN = 200;

const fold = (s: string) => s.toLocaleLowerCase().normalize('NFKD').replace(/[ً-ٰٟ]/g, '');

/** A searchable single choice: a field-looking button that opens a list with a search box. */
export function Combobox<V extends string | number>({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyText,
  nullLabel,
  disabled,
  invalid,
  loading,
  id,
  className,
  contentClassName,
  leading,
  trailing,
  triggerRef,
  ariaLabel,
}: ComboboxProps<V>) {
  const { invalid: isInvalid, ...wiring } = useFieldWiring(id, invalid);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const container = useLayerContainer();

  const selected = options.find((o) => o.value === value) ?? null;

  type Row = { value: V | null; label: string; detail?: string; icon?: ReactNode };
  const rows: Row[] = useMemo(() => {
    const q = fold(query.trim());
    const matched = q
      ? options.filter((o) => fold(`${o.label} ${o.detail ?? ''} ${o.keywords ?? ''}`).includes(q))
      : options;
    const head: Row[] = nullLabel && !q ? [{ value: null, label: nullLabel }] : [];
    return [...head, ...matched.slice(0, MAX_SHOWN)];
  }, [options, query, nullLabel]);

  const choose = (row: Row) => {
    onChange(row.value);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.max(0, Math.min(rows.length - 1, active + (event.key === 'ArrowDown' ? 1 : -1)));
      setActive(next);
      listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = rows[active];
      if (row) choose(row);
    }
  };

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setQuery('');
          const index = rows.findIndex((r) => r.value === value);
          setActive(index < 0 ? 0 : index);
        }
      }}
    >
      <PopoverPrimitive.Trigger asChild disabled={disabled}>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listId}
          aria-haspopup="listbox"
          {...wiring}
          className={cn(
            'flex h-control-md w-full min-w-0 items-center gap-2 rounded-md border bg-surface pe-2.5 ps-3 text-start text-body text-ink outline-none transition-[border-color,box-shadow] duration-quick ease-drive',
            'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-disabled',
            isInvalid ? 'border-hazard shadow-[0_0_0_1px_var(--vlm-hazard)]' : 'border-line-input focus-visible:border-focus focus-visible:shadow-[0_0_0_1px_var(--vlm-focus)] data-[state=open]:border-focus',
            className,
          )}
        >
          {leading}
          {selected?.icon}
          <span className={cn('min-w-0 flex-1 truncate', !selected && value === null && !nullLabel && 'text-ink-subtle')}>
            {selected ? selected.label : value === null && nullLabel ? nullLabel : placeholder}
          </span>
          {selected?.detail && <span className="hidden truncate font-mono text-[12px] text-ink-subtle sm:inline">{selected.detail}</span>}
          {trailing ?? <ChevronDown size={16} aria-hidden="true" className="shrink-0 text-ink-subtle" />}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal container={container}>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            (e.currentTarget as HTMLElement | null)?.querySelector('input')?.focus();
          }}
          className={cn(
            'vlm-pop z-dropdown grid max-h-[min(380px,var(--radix-popover-content-available-height))] w-[max(var(--radix-popover-trigger-width),280px)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-md border border-line bg-surface-raised shadow-3',
            contentClassName,
          )}
        >
          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search size={16} aria-hidden="true" className="shrink-0 text-ink-subtle" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              aria-controls={listId}
              aria-activedescendant={rows[active] ? `${listId}-${active}` : undefined}
              className="h-10 min-w-0 flex-1 border-0 bg-transparent text-body text-ink outline-none placeholder:text-ink-subtle"
            />
          </div>
          <ul ref={listRef} id={listId} role="listbox" className="min-h-0 overflow-y-auto p-1">
            {loading && rows.length === 0 && <li className="px-2.5 py-2 text-body-sm text-ink-subtle">…</li>}
            {!loading && rows.length === 0 && <li className="px-2.5 py-2 text-body-sm text-ink-subtle">{emptyText}</li>}
            {rows.map((row, i) => {
              const isSelected = row.value === value;
              return (
                <li
                  key={row.value === null ? '∅' : String(row.value)}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseMove={() => setActive(i)}
                  onClick={() => choose(row)}
                  className={cn(
                    'flex min-h-9 cursor-pointer select-none items-center gap-2.5 rounded-sm py-1.5 pe-8 ps-2.5 text-body-sm text-ink',
                    i === active && 'bg-hover',
                    'relative',
                  )}
                >
                  {row.icon}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{row.label}</span>
                    {row.detail && <span className="block truncate font-mono text-[12px] text-ink-subtle">{row.detail}</span>}
                  </span>
                  {isSelected && <Check size={16} aria-hidden="true" className="absolute end-2.5 text-ink" />}
                </li>
              );
            })}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
